import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Slider from 'react-slick';
import { decideInitialSlide } from '../../../../app/functions';
import SliderArrow from '../../shared/slider_arrow';
import CategoryItem from '../../shared/category_item';
import './index.scss';

const CategorySlider = forwardRef(({
    selectedCategory,
    availableCategories,
    onCategorySelect,
    onCategoryRemove,
    customSliderSettings = {},
    reinitializeOnDataChange = true,
}, slickRef) => {
    const location = useLocation();

    const deviceState = useSelector((store) => store.state.deviceState);

    if (slickRef === null) {
        slickRef = useRef(null);
    }

    const sharedSliderSettings = {
        rtl: true,
        infinite: false,
        slidesToScroll: 1,
        nextArrow: <SliderArrow />,
        prevArrow: <SliderArrow />,
        ...customSliderSettings?.shared,
    };

    const getSliderSettings = () => {
        switch (deviceState) {
            case 'mobile':
                return {
                    ...sharedSliderSettings,
                    ...customSliderSettings?.mobile,
                    initialSlide: decideInitialSlide(
                        availableCategories, customSliderSettings?.mobile?.slidesToShow ?? 4, selectedCategory
                    ),
                    slidesToShow: customSliderSettings?.mobile?.slidesToShow ?? 4,
                };

            case 'tablet':
                return {
                    ...sharedSliderSettings,
                    ...customSliderSettings?.tablet,
                    initialSlide: decideInitialSlide(
                        availableCategories, customSliderSettings?.tablet?.slidesToShow ?? 5, selectedCategory
                    ),
                    slidesToShow: customSliderSettings?.tablet?.slidesToShow ?? 5,
                };

            case 'desktop':
                return {
                    ...sharedSliderSettings,
                    ...customSliderSettings?.desktop,
                    initialSlide: decideInitialSlide(
                        availableCategories, customSliderSettings?.desktop?.slidesToShow ?? 6, selectedCategory
                    ),
                    slidesToShow: customSliderSettings?.desktop?.slidesToShow ?? 6,
                };

            default:
                return {
                    ...sharedSliderSettings,
                    ...customSliderSettings?.desktopLarge,
                    initialSlide: decideInitialSlide(
                        availableCategories, customSliderSettings?.desktopLarge?.slidesToShow ?? 7, selectedCategory
                    ),
                    slidesToShow: customSliderSettings?.desktopLarge?.slidesToShow ?? 7,
                };
        }
    };

    const [sliderSettings, setSliderSettings] = useState(getSliderSettings);

    const handleCategoryClick = (categoryId) => {
        if (selectedCategory === categoryId) {
            onCategoryRemove(categoryId);
        } else {
            onCategorySelect(categoryId);
        }
    };

    const effectInputsList = [location.pathname, deviceState];

    if (reinitializeOnDataChange) {
        effectInputsList.push(availableCategories);
    }

    // Update slider settings if the location path / device type / available categories change
    useEffect(() => {
        const newSliderSettings = getSliderSettings();
        setSliderSettings(newSliderSettings);

        // Return the slider to initial state whenever the state changes
        setTimeout(() => {
            if (slickRef.current) {
                slickRef.current.slickGoTo(newSliderSettings.initialSlide);
            }
        }, 1000);
    }, effectInputsList);

    return (
        <div className="slider-category-select">
            <Slider className="categories-slider" ref={slickRef} {...sliderSettings}>
                {availableCategories.map((categoryData) => categoryData && (
                    <CategoryItem
                        key={categoryData.id}
                        data={categoryData}
                        isSelected={categoryData.id === selectedCategory}
                        onCategoryClick={handleCategoryClick}
                    />
                ))}
            </Slider>
        </div>
    );
});

export default CategorySlider;
